<script setup>
/**
 * @description 商城首页分类导航组件
 * @reason 首页分类导航，方便用户快速进入特定产品分类
 */

// 1. Imports
import { ref, computed, onMounted, onUnmounted, resolveComponent } from 'vue'
import { getOssUrl } from '@/utils'
import { useMediaQuery } from '@/composables/useMediaQuery'
import { useCategoryStore } from '@/store/common/category'
import { useUserStore } from '~/store/common/user'
import { getAllSecondaryCategories, isSecondaryCategoryVisible } from '~/utils/store/categoryTree'
import { trackClickEvent } from '~/composables/usePageTracking'
import { createPublicContentSsrRequestConfig } from '~/utils/website/ssrRequestPolicy'
import BaseCardCarousel from '~/components/base/CardCarousel.vue'
import BaseImage from '~/components/base/Image.vue'

// 2. Props
const props = defineProps({
  /**
   * @description Optional category source; category pages pass second-level categories for mobile design fidelity.
   * @type {Array}
   * @default []
   */
  categoriesData: {
    type: Array,
    default: () => [],
  },
  /**
   * @description Active category id for visual highlight.
   * @type {string|number}
   * @default ''
   */
  activeId: {
    type: [String, Number],
    default: '',
  },
  /**
   * @description Parent category id used when rendering a second-level category nav.
   * @type {string|number}
   * @default ''
   */
  parentCategoryId: {
    type: [String, Number],
    default: '',
  },
  /**
   * @description When true, clicking a category emits 'categoryClick' instead of navigating.
   * @type {boolean}
   * @default false
   */
  interceptClick: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 分类点击埋点配置：{ categoryClickEvent, pageName }
   * @type {Object|null}
   * @default null
   */
  tracking: {
    type: Object,
    default: null,
  },
})

// 3. Emits
const emit = defineEmits(['categoryClick'])

// 4. Refs/Reactive State
const { isMobile } = useMediaQuery()
const categoryStore = useCategoryStore()
const userStore = useUserStore()
const route = useRoute()
const { data: initialCategoryTree } = await useAsyncData(
  'store-home-category-navigation',
  async () => {
    if (import.meta.client) return []
    if (userStore.isLoggedIn) return []

    try {
      return await categoryStore.fetchCategoryInfo({
        authKey: 'guest',
        requestConfig: createPublicContentSsrRequestConfig({ timeout: 3_000 }),
      })
    } catch (error) {
      console.error('[CategoryNav] Failed to fetch SSR category tree:', error)
      return []
    }
  },
  usePublicContentAsyncDataOptions({ default: () => [] })
)

if (
  (import.meta.server || useNuxtApp().isHydrating) &&
  Array.isArray(initialCategoryTree.value) &&
  initialCategoryTree.value.length > 0
) {
  categoryStore.$patch({
    categoryTree: initialCategoryTree.value,
    fetched: true,
    fetchedAuthKey: 'guest',
    fetchedClient: false,
    loading: false,
  })
}
let gsapInstance = null
let gsapLoadPromise = null
let isUnmounted = false
const animatedTargets = new Set()

const loadGsap = async () => {
  if (!import.meta.client) return null
  if (gsapInstance) return gsapInstance
  gsapLoadPromise ??= import('gsap').then(({ gsap }) => {
    gsapInstance = gsap
    return gsap
  })
  return gsapLoadPromise
}

/**
 * @description 获取分类列表数据
 * @reason 组件在客户端挂载，直接使用 store 的响应式数据
 */
const loading = computed(() => categoryStore.loading && categoryStore.categoryList.length === 0)

onMounted(() => {
  isUnmounted = false
  categoryStore.fetchCategoryInfo().catch((error) => {
    console.error('[CategoryNav] Failed to fetch category tree:', error)
  })
})

const categories = computed(() => {
  const hasScopedParent = String(props.parentCategoryId || '').trim() !== ''
  const sourceCategories =
    props.categoriesData.length > 0
      ? props.categoriesData.filter(isSecondaryCategoryVisible)
      : hasScopedParent
        ? []
        : getAllSecondaryCategories(categoryStore.categoryList)

  return sourceCategories.map((item) => ({
    name: item.categoryName,
    image: getOssUrl(item.pic),
    id: item.categoryId,
    parentCategoryId: item.parentCategoryId || props.parentCategoryId || '',
  }))
})

const carouselGap = computed(() => (isMobile.value ? 16 : 32))

const activeCategoryId = computed(() => {
  if (props.activeId) return props.activeId
  if (route.path.startsWith('/store/products')) {
    return route.query.secondaryCategoryId || route.query.categoryId || ''
  }
  if (!route.path.startsWith('/store/category/')) return ''
  return Array.isArray(route.params.id) ? route.params.id[0] : route.params.id || ''
})

// 5. Methods
/**
 * @description Build the proper category entry route for home and category pages.
 * @param {string|number} categoryId - Target category id.
 * @returns {string|Object}
 */
const buildCategoryLink = (categoryId, parentCategoryId = '') => {
  if (props.parentCategoryId) {
    return {
      path: '/store/products',
      query: {
        categoryId: String(props.parentCategoryId),
        secondaryCategoryId: String(categoryId),
      },
    }
  }

  if (parentCategoryId) {
    return {
      path: '/store/products',
      query: {
        categoryId: String(parentCategoryId),
        secondaryCategoryId: String(categoryId),
      },
    }
  }

  return {
    path: '/store/products',
    query: { categoryId: String(categoryId) },
  }
}

const categoryItemComponent = computed(() =>
  props.interceptClick ? 'button' : resolveComponent('NuxtLink')
)

const getCategoryItemAttrs = (item) => {
  if (props.interceptClick) {
    return { type: 'button' }
  }
  return { to: buildCategoryLink(item.id, item.parentCategoryId) }
}

/**
 * @description 处理分类点击：interceptClick 时阻止导航、改为 emit
 * @param {string|number} categoryId
 * @param {string|number} parentCategoryId
 * @param {MouseEvent} event
 */
const handleCategoryClick = (categoryId, parentCategoryId, event) => {
  const category = categories.value.find((item) => String(item.id) === String(categoryId))
  if (props.tracking?.categoryClickEvent && category) {
    trackClickEvent({
      pvEvent: props.tracking.categoryClickEvent,
      targetType: 'mall_category',
      targetId: String(categoryId),
      targetName: category.name || '',
      pageName: props.tracking.pageName || '商城首页',
      properties: {
        category_id: String(categoryId),
        category_name: category.name || '',
        parent_category_id: String(parentCategoryId || ''),
      },
    })
  }

  if (props.interceptClick) {
    event.preventDefault()
    emit('categoryClick', String(categoryId))
  }
}

/**
 * @description 鼠标移入时放大
 * @param {MouseEvent} e
 */
const handleMouseEnter = (event) => {
  const target = event.currentTarget
  animatedTargets.add(target)
  void loadGsap().then((gsap) => {
    if (!gsap || isUnmounted) return
    gsap.to(target, {
      scale: 1.05,
      duration: 0.3,
      ease: 'power2.out',
    })
  })
}

/**
 * @description 鼠标移出时恢复
 * @param {MouseEvent} e
 */
const handleMouseLeave = (event) => {
  const target = event.currentTarget
  animatedTargets.add(target)
  void loadGsap().then((gsap) => {
    if (!gsap || isUnmounted) return
    gsap.to(target, {
      scale: 1,
      duration: 0.3,
      ease: 'power2.out',
    })
  })
}

// 6. Lifecycle Hooks
onUnmounted(() => {
  isUnmounted = true
  if (gsapInstance && animatedTargets.size > 0) {
    gsapInstance.killTweensOf([...animatedTargets])
  }
  animatedTargets.clear()
})
</script>

<template>
  <section v-if="loading || categories.length > 0" class="category-nav base-container">
    <BaseCardCarousel
      :gap="carouselGap"
      :mobile-gap="4"
      :padding-bottom="12"
      center-when-not-overflowing
      overlay-color="rgba(0, 0, 0, 0.08)"
      overlay-width="6px"
    >
      <template v-if="loading">
        <div v-for="i in 7" :key="i" class="category-nav__item is-skeleton">
          <a-skeleton animation>
            <div class="category-nav__icon-box">
              <a-skeleton-shape class="category-nav__skeleton-shape" shape="square" size="large" />
            </div>
          </a-skeleton>
        </div>
      </template>
      <template v-else>
        <component
          :is="categoryItemComponent"
          v-for="item in categories"
          :key="item.id"
          v-bind="getCategoryItemAttrs(item)"
          class="category-nav__item"
          :class="{ 'category-nav__item--active': String(activeCategoryId) === String(item.id) }"
          :aria-label="item.name"
          @click="handleCategoryClick(item.id, item.parentCategoryId, $event)"
          @mouseenter="handleMouseEnter"
          @mouseleave="handleMouseLeave"
        >
          <div class="category-nav__icon-box">
            <BaseImage
              :src="item.image"
              :alt="item.name"
              class="category-nav__image"
              :preload="true"
            />
          </div>
          <span class="category-nav__name">{{ item.name }}</span>
        </component>
      </template>
    </BaseCardCarousel>
  </section>
</template>

<style lang="scss" scoped>
.category-nav {
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 20px;
  padding-bottom: 36px;

  @include respond-to-max('lg') {
    padding-top: 12px;
    padding-bottom: 12px;
  }

  &__item {
    width: fit-content;
    min-width: $store-category-nav-item-min-width;
    max-width: $store-category-nav-item-max-width;
    min-height: 96px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    cursor: pointer;
    flex-shrink: 0;
    transition:
      background-color 0.3s ease,
      transform 0.3s ease;
    border-radius: $radius-6;
    text-decoration: none;
    color: inherit;
    border: 0;
    background: transparent;
    font: inherit;
    box-sizing: border-box;
    padding: $spacing-md $spacing-md $spacing-xs;

    @include respond-to-max('lg') {
      min-width: $store-category-nav-item-min-width-mobile;
      max-width: $store-category-nav-item-max-width-mobile;
      min-height: 84px;
      gap: 4px;
      border-radius: $radius-6;
      padding: $spacing-xs;
    }

    &:hover {
      background-color: rgba($text-subtitle-color, 0.16);
    }

    &--active {
      background-color: rgba($text-subtitle-color, 0.12);
    }

    &.is-skeleton {
      cursor: default;
      &:hover {
        background-color: transparent;
      }
      :deep(.arco-skeleton) {
        width: 100%;
        display: flex;
        justify-content: center;
      }
      :deep(.arco-skeleton-shape) {
        background-color: $border-dashed-color;
      }

      :deep(.category-nav__skeleton-shape) {
        width: 100px;
        height: 100px;
        border-radius: 6px;
      }
    }
  }

  &__icon-box {
    width: 88px;
    height: 88px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background-color: transparent;

    @include respond-to-max('lg') {
      width: 44px;
      height: 44px;
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: contain;

    :deep(.base-image__inner img) {
      object-fit: contain;
    }
  }

  &__name {
    width: auto;
    max-width: $store-category-nav-name-max-width;
    color: $text-title-color;
    font-size: 14px;
    line-height: 22px;
    text-align: center;
    @include text-ellipsis;

    @include respond-to-max('lg') {
      font-size: 12px;
      line-height: 20px;
    }
  }
}
</style>
